import { useState } from "react";
import { useNavigate } from "react-router";
import {
  CONTRACTED_BY,
  CONTRACTED_BY_LABELS,
  INDEX_METHOD_LABELS,
  INDEX_METHODS,
  type ContractDetailDto,
  type ContractedBy,
  type ContractStatus,
  type IndexMethod,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useBuildings } from "../../lib/buildingContext";
import { useClientContext } from "../../lib/clientContext";
import { useToast } from "../../lib/toast";
import { NO_PARTY, PartyPicker, type Party } from "../directory/PartyPicker";
import { errMsg, useContractsRefresh } from "./useContracts";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
const intOrNull = (v: string) => (v.trim() === "" ? null : Math.max(0, Math.round(Number(v))));

/** Nieuw contract of de afspraken van een contract bewerken (docs/CONTRACTEN.md). */
export function ContractDialog({
  contract: c,
  buildingId,
  onClose,
}: {
  contract?: ContractDetailDto;
  /** Vanuit Gebouw › Contracten: dit gebouw en zijn klant staan al gekozen. */
  buildingId?: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useContractsRefresh();
  const buildings = useBuildings();
  const { id: ctxClient, clients } = useClientContext();
  const fromBuilding = buildingId ? buildings.data?.find((b) => b.id === buildingId) : undefined;
  const [name, setName] = useState(c?.name ?? "");
  const [reference, setReference] = useState(c?.reference ?? "");
  const [clientId, setClientId] = useState(c?.client.id ?? fromBuilding?.clientId ?? ctxClient ?? "");
  const [picked, setPicked] = useState<Set<string>>(new Set(buildingId ? [buildingId] : []));
  const [supplier, setSupplier] = useState<Party>(
    c?.supplier ? { ...NO_PARTY, companyId: c.supplier.id, personId: c.person?.id ?? null } : NO_PARTY,
  );
  const [contractedBy, setContractedBy] = useState<ContractedBy>(c?.contractedBy ?? "organization");
  const [status, setStatus] = useState<ContractStatus>(c?.status ?? "active");
  const [startsOn, setStartsOn] = useState(c?.startsOn ?? today());
  const [endsOn, setEndsOn] = useState(c?.endsOn ?? "");
  const [autoRenew, setAutoRenew] = useState(c?.autoRenew ?? true);
  const [renewMonths, setRenewMonths] = useState(c?.renewMonths != null ? String(c.renewMonths) : "12");
  const [noticeMonths, setNoticeMonths] = useState(c?.noticeMonths != null ? String(c.noticeMonths) : "3");
  const [indexMethod, setIndexMethod] = useState<IndexMethod>(c?.indexMethod ?? "org");
  const [indexPercent, setIndexPercent] = useState(c?.indexPercent != null ? String(c.indexPercent) : "");
  const [responseTimes, setResponseTimes] = useState(c?.responseTimes ?? "");
  const [summary, setSummary] = useState(c?.summary ?? "");
  const [documentId, setDocumentId] = useState(c?.document?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);

  const ofClient = (buildings.data ?? [])
    .filter((b) => b.clientId === clientId)
    .sort((a, b) => a.code.localeCompare(b.code));
  const chosen = [...picked].filter((id) => ofClient.some((b) => b.id === id));
  const noSupplier = !supplier.companyId;
  const badDates = !!endsOn && endsOn < startsOn;
  const pctNum = indexPercent.trim() ? Number(indexPercent.replace(",", ".")) : null;
  const invalid =
    !name.trim() ||
    !clientId ||
    (!c && chosen.length === 0) ||
    noSupplier ||
    !startsOn ||
    badDates ||
    (pctNum !== null && Number.isNaN(pctNum));

  async function save() {
    setTried(true);
    if (invalid) return;
    setBusy(true);
    const body = {
      name,
      reference: reference || null,
      clientId,
      contractedBy,
      supplierCompanyId: supplier.companyId,
      supplierPersonId: supplier.personId,
      startsOn,
      endsOn: endsOn || null,
      autoRenew,
      renewMonths: autoRenew ? intOrNull(renewMonths) : null,
      noticeMonths: intOrNull(noticeMonths),
      indexMethod,
      indexPercent: indexMethod === "fixed" ? pctNum : null,
      responseTimes: responseTimes || null,
      summary: summary || null,
    };
    try {
      if (c) {
        const d = await api<ContractDetailDto>(`/contracts/${c.id}`, {
          method: "PATCH",
          body: { ...body, status, documentId: documentId || null },
        });
        await refresh(d);
        toast("Contract opgeslagen");
        onClose();
      } else {
        const id = crypto.randomUUID();
        const d = await api<ContractDetailDto>("/contracts", {
          method: "POST",
          body: { id, ...body, buildingIds: chosen },
        });
        await refresh(d);
        toast("Contract vastgelegd");
        onClose();
        navigate(`/contracten/${id}?tab=posten`);
      }
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={c ? "Afspraken bewerken" : "Nieuw contract"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
            {busy ? "Bezig met opslaan…" : c ? "Opslaan" : "Vastleggen"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Naam *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. Liftonderhoud" />
        {tried && !name.trim() && <span className="hint err">Vul een naam in</span>}
      </label>
      <PartyPicker
        id="contract-supplier"
        label="Leverancier *"
        value={supplier}
        onChange={(p) => setSupplier(p)}
        savedName={c?.person ? `${c.person.name} (${c.supplier?.name ?? ""})` : c?.supplier?.name}
        buildingId={buildingId ?? chosen[0]}
        clearable={false}
        error={tried && noSupplier ? "Kies een leverancier" : undefined}
      />
      <div className="grid2">
        <label className="field">
          <span>Contractnummer</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="van de leverancier" />
        </label>
        <label className="field">
          <span>Afgesloten door</span>
          <select value={contractedBy} onChange={(e) => setContractedBy(e.target.value as ContractedBy)}>
            {CONTRACTED_BY.map((k) => (
              <option key={k} value={k}>
                {CONTRACTED_BY_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span>Klant *</span>
        <select
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            setPicked(new Set());
          }}
          disabled={!!buildingId && !c}
        >
          <option value="">Kies een klant</option>
          {(clients ?? []).map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
      </label>
      {!c && clientId && (
        <fieldset className="checklist">
          <legend>Gebouwen *</legend>
          {ofClient.length ? (
            <>
              {ofClient.map((b) => (
                <label key={b.id}>
                  <input
                    type="checkbox"
                    checked={picked.has(b.id)}
                    onChange={() =>
                      setPicked((p) => {
                        const n = new Set(p);
                        if (n.has(b.id)) n.delete(b.id);
                        else n.add(b.id);
                        return n;
                      })
                    }
                  />
                  <span>
                    <span className="code">{b.code}</span> {b.name}
                  </span>
                </label>
              ))}
            </>
          ) : (
            <p className="muted small">Deze klant heeft nog geen gebouwen.</p>
          )}
          {tried && chosen.length === 0 && <span className="hint err">Kies minstens één gebouw</span>}
        </fieldset>
      )}
      <div className="grid2">
        <label className="field">
          <span>Ingangsdatum *</span>
          <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </label>
        <label className="field">
          <span>Einddatum</span>
          <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          {badDates ? (
            <span className="hint err">De einddatum ligt voor de ingangsdatum</span>
          ) : (
            !endsOn && <span className="hint">Leeg = onbepaalde tijd</span>
          )}
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
        <span>Stilzwijgend verlengen</span>
      </label>
      <div className="grid2">
        {autoRenew && (
          <label className="field">
            <span>Verlengen met (maanden)</span>
            <input inputMode="numeric" value={renewMonths} onChange={(e) => setRenewMonths(e.target.value)} />
          </label>
        )}
        <label className="field">
          <span>Opzegtermijn (maanden)</span>
          <input inputMode="numeric" value={noticeMonths} onChange={(e) => setNoticeMonths(e.target.value)} />
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>Indexatie</span>
          <select value={indexMethod} onChange={(e) => setIndexMethod(e.target.value as IndexMethod)}>
            {INDEX_METHODS.map((k) => (
              <option key={k} value={k}>
                {INDEX_METHOD_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        {indexMethod === "fixed" && (
          <label className="field">
            <span>Percentage per jaar</span>
            <input inputMode="decimal" value={indexPercent} onChange={(e) => setIndexPercent(e.target.value)} />
          </label>
        )}
      </div>
      {c && (
        <div className="grid2">
          <label className="field">
            <span>Stand</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as ContractStatus)}>
              <option value="draft">Concept</option>
              <option value="active">Lopend</option>
              <option value="cancelled">Opgezegd</option>
            </select>
          </label>
          <label className="field">
            <span>Contractdocument</span>
            <select value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
              <option value="">Geen</option>
              {c.documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
            {!c.documents.length && (
              <span className="hint">Zet het contract in het dossier als Onderhoudscontract om het te koppelen.</span>
            )}
          </label>
        </div>
      )}
      <label className="field">
        <span>Responstijden</span>
        <textarea
          rows={2}
          value={responseTimes}
          onChange={(e) => setResponseTimes(e.target.value)}
          placeholder="bijv. storing hoog binnen 4 uur, overig volgende werkdag"
        />
      </label>
      <label className="field">
        <span>Afspraken</span>
        <textarea
          rows={4}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Wat valt eronder, wat niet, bijzonderheden"
        />
      </label>
    </Modal>
  );
}
